/* eslint-disable */
/**
 * 美國 EPA 綜合油耗（comb08 = combined MPG）→ 公制 L/100km
 * MPG 為美制；235.214583 / MPG = L/100km
 */
function mpgUsToLitersPer100km(mpg) {
  const m = Number(mpg);
  if (!m || m <= 0) return null;
  return Math.round((235.214583 / m) * 10) / 10;
}

/**
 * EPA menu XML：menuItem 內為 <text> / <value> 子元素（唔係 attribute）
 * @returns {{ value: string, text: string }[]}
 */
export function parseEpaMenuItemsXml(xmlText) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, "text/xml");
    if (doc.querySelector("parsererror")) return [];
    const items = doc.getElementsByTagName("menuItem");
    const out = [];
    for (let i = 0; i < items.length; i++) {
      const el = items[i];
      const textEl = el.querySelector("text");
      const valueEl = el.querySelector("value");
      let value = (valueEl?.textContent || "").trim();
      let text = (textEl?.textContent || "").trim();
      if (!value) value = (el.getAttribute("value") || "").trim();
      if (!text) text = (el.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) text = value;
      if (value) out.push({ value, text });
    }
    return out;
  } catch (e) {
    return [];
  }
}

async function fetchEpaMenuPath(path) {
  const res = await fetch(`https://www.fueleconomy.gov/ws/rest/vehicle/menu/${path}`);
  if (!res.ok) throw new Error("EPA menu");
  return parseEpaMenuItemsXml(await res.text());
}

/**
 * @param {number} [minYear]
 * @param {number} [maxYear]
 */
export async function fetchEpaYearOptions(minYear = 1984, maxYear = 2030) {
  const rows = await fetchEpaMenuPath("year");
  return rows
    .filter((r) => {
      const n = parseInt(r.value, 10);
      return n >= minYear && n <= maxYear;
    })
    .sort((a, b) => parseInt(b.value, 10) - parseInt(a.value, 10));
}

export async function fetchEpaMakeOptions(year) {
  const y = parseInt(String(year), 10);
  if (!y) return [];
  return fetchEpaMenuPath(`make?year=${y}`);
}

export async function fetchEpaModelOptions(year, make) {
  const y = parseInt(String(year), 10);
  const mk = encodeURIComponent((make || "").trim());
  if (!y || !mk) return [];
  return fetchEpaMenuPath(`model?year=${y}&make=${mk}`);
}

export async function fetchEpaTrimOptions(year, make, model) {
  const y = parseInt(String(year), 10);
  const mk = encodeURIComponent((make || "").trim());
  const md = encodeURIComponent((model || "").trim());
  if (!y || !mk || !md) return [];
  return fetchEpaMenuPath(`options?year=${y}&make=${mk}&model=${md}`);
}

/**
 * 用 EPA 車輛 ID（選單「配置」value）直接讀 comb08
 */
export async function fetchEpaLitersPer100kmForVehicleId(vid) {
  const id = String(vid || "").trim();
  if (!id) return null;
  try {
    const vRes = await fetch(`https://www.fueleconomy.gov/ws/rest/vehicle/${id}`);
    if (!vRes.ok) return null;
    const vText = await vRes.text();
    const parser = new DOMParser();
    const vDoc = parser.parseFromString(vText, "text/xml");
    const comb = vDoc.querySelector("comb08");
    const mpg = comb ? parseFloat(comb.textContent.trim()) : NaN;
    return mpgUsToLitersPer100km(mpg);
  } catch (e) {
    return null;
  }
}

/**
 * 以年／品牌／型號（EPA 型號名）取第一個配置嘅油耗（向後相容）
 */
export async function fetchEpaLitersPer100km(year, make, model) {
  const trims = await fetchEpaTrimOptions(year, make, model);
  if (!trims.length) return null;
  return fetchEpaLitersPer100kmForVehicleId(trims[0].value);
}

/**
 * 以車型類別＋年份粗略估算（API 失敗時）
 */
export function heuristicLitersPer100km(vehicleType, vehicleYear) {
  const y = parseInt(String(vehicleYear), 10);
  const year = y >= 1980 && y <= 2030 ? y : 2018;
  const age = Math.max(0, new Date().getFullYear() - year);
  const bump = Math.min(2.2, age * 0.12);
  let base = 9.2;
  if (vehicleType === "SUV") base = 11.8;
  if (vehicleType === "Van") base = 13.2;
  if (vehicleType === "電動車") return null;
  return Math.round((base + bump) * 10) / 10;
}

/**
 * @param {{ vtype: string, vehicleYear: string|number, vehicleMake: string, vehicleModel: string, manualLiters100?: string|number, vehicleEpaId?: string }} p
 */
export async function resolveDriverLitersPer100km(p) {
  const { vtype, vehicleYear, vehicleMake, vehicleModel, manualLiters100, vehicleEpaId } = p;
  if (vtype === "電動車") {
    return { litersPer100km: null, fuelEconomySource: "ev" };
  }
  const manual = parseFloat(String(manualLiters100 || "").replace(",", "."));
  if (manual > 0 && manual < 40) {
    return { litersPer100km: Math.round(manual * 10) / 10, fuelEconomySource: "manual" };
  }
  const id = String(vehicleEpaId || "").trim();
  if (id) {
    const byId = await fetchEpaLitersPer100kmForVehicleId(id);
    if (byId != null && byId > 0) {
      return { litersPer100km: byId, fuelEconomySource: "epa" };
    }
  }
  const epa = await fetchEpaLitersPer100km(vehicleYear, vehicleMake, vehicleModel);
  if (epa != null && epa > 0) {
    return { litersPer100km: epa, fuelEconomySource: "epa" };
  }
  const h = heuristicLitersPer100km(vtype, vehicleYear);
  return { litersPer100km: h, fuelEconomySource: h != null ? "heuristic" : "unknown" };
}
